import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface } from '@sport-activities/sdk';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { PaginationTypeEnum } from '~/attendee/components/shared/pagination/enums/pagination-type.enum';
import { getDefaultFilter } from '~/attendee/helpers/activity/default-filter-params.helper';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';
import { NavigatorService } from '~/attendee/service/navigator.service';
import { PaginatedSearchService } from '~/attendee/service/paginated-search.service';
import {
  ResultPageFilters,
  ResultPageFilterService,
} from '~/attendee/service/result-page-filter.service';
import { BannerSectionCms } from '~/cms/interfaces/homepage.cms.interface';
import { CmsService } from '~/cms/services/cms/cms.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { deepMerge } from '~/core/helpers/object/deep-merge/deep-merge.helper';
import { Activity } from '~/core/model/activity.model';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MapsService } from '~/core/services/maps/maps.service';
import { TagManagerService } from '~/core/services/tag-manager/tag-manager.service';

const EXTERNAL_LOADER_KEY = 'loadExternal';

@Component({
  selector: 'app-attendee-result-page-external',
  templateUrl: './external.component.html',
})
export class ResultPageExternalComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  public hasLoadedExternal = new BehaviorSubject<boolean>(false);

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly paginationType = PaginationTypeEnum;

  private paramFilters: ResultPageFilters = {};

  public hasLoadMoreButton: boolean;
  public isLoading = true;
  public items: ActivityInterface[] = [];
  public totalItems: number;
  public pagination: number;
  public sportGroupBanner: BannerSectionCms;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  public placeResult: any;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject('ActivitySearchService')
    private readonly searchService: PaginatedSearchService<
      Activity,
      ActivityFilterParamsInterface
    >,
    private readonly resultPageFilterService: ResultPageFilterService,
    private readonly mapsService: MapsService,
    private readonly navigatorService: NavigatorService,
    private readonly translateService: TranslateService,
    private readonly cms: CmsService,
    private readonly customHeadTag: CustomHeadTagService,
    private readonly tagManager: TagManagerService,
    private readonly loaderService: LoaderService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.customHeadTag.destroyMicrodata();
  }

  public ngOnInit(): void {
    this.setQueryParamDefault();

    this.loaderService.start(EXTERNAL_LOADER_KEY);

    if (isPlatformBrowser(this.platformId)) {
      this.mapsService.initMapScript();
    }

    this.subscriptions.add(
      this.mapsService.mapScriptInitialized().subscribe((woosmapLoaded) => {
        if (woosmapLoaded) {
          this.subscriptions.add(this.getResult());
        }
      })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(EXTERNAL_LOADER_KEY);
      })
    );

    this.setBreadcrumb();
  }

  private setQueryParamDefault(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.navigatorService.buildUrlTree();
  }

  private getResult(): void {
    this.resultPageFilterService.filters$
      .pipe(
        mergeMap((paramFilters) => {
          this.loaderService.start(EXTERNAL_LOADER_KEY);
          this.pagination = 0;

          if (paramFilters?.placeId) {
            return this.mapsService.getPlace(paramFilters.placeId).pipe(
              map((placeResult) => ({
                paramFilters,
                placeResult,
              }))
            );
          }

          return of({ paramFilters, placeResult: null });
        }),
        mergeMap(({ paramFilters, placeResult }) => {
          const itemsPerPage = appConfig.pagination.result;

          const filters = deepMerge<ActivityFilterParamsInterface>(
            this.defaultFilters,
            {
              sportGroups: paramFilters.sportGroups,
              ageRange: paramFilters.ageRange,
              timeslots: {
                startDate:
                  paramFilters.startDate ||
                  this.defaultFilters.timeslots.startDate,
                endDate: paramFilters.endDate,
                priceRange: paramFilters.priceRange,
              },
              pagination: {
                from: (paramFilters?.page - 1) * itemsPerPage || 0,
                limit: itemsPerPage,
              },
            }
          );

          if (placeResult) {
            filters.geo = {
              distance: paramFilters.radius,
              longitude: +placeResult?.geometry.location.lng,
              latitude: +placeResult?.geometry.location.lat,
            };
            this.placeResult = {
              distance: paramFilters.radius,
              longitude: +placeResult?.geometry.location.lng,
              latitude: +placeResult?.geometry.location.lat,
            };
          }

          return combineLatest([
            this.searchService.search(filters),
            filters.sportGroups
              ? this.cms.getSportBanner(+filters.sportGroups)
              : of(null),
          ]).pipe(
            map(([results, sportBanner]) => ({
              results,
              sportBanner,
              paramFilters,
            }))
          );
        })
      )
      .subscribe({
        next: (response) => {
          this.items = response.results?.items;
          this.paramFilters = response.paramFilters;
          this.sportGroupBanner = response.sportBanner?.banner[0];

          if (response.results.total === 0) {
            this.tagManager.pushNoResult('Activités');
          }
          this.loaderService.stop(EXTERNAL_LOADER_KEY);

          this.totalItems = response.results.total;
          this.pagination = Math.ceil(
            response.results.total / appConfig.pagination.result
          );

          if (
            this.items.length === appConfig.pagination.result ||
            this.paramFilters.page > 1
          ) {
            this.hasLoadedExternal.next(true);
          } else {
            this.totalItems = response.results.total;
            this.pagination = 0;
          }
        },
        error: () => {
          this.loaderService.stop(EXTERNAL_LOADER_KEY);
        },
      });
  }

  private get defaultFilters(): ActivityFilterParamsInterface {
    const defaultFilter = getDefaultFilter(false);

    defaultFilter.includeNextDates = true;
    defaultFilter.isExternalBookable = true;

    return defaultFilter;
  }

  public get tips(): string[] {
    return this.translateService
      .instant('attendee.result_page.activites_tips.text')
      .split('✓');
  }

  private setBreadcrumb(): void {
    this.customHeadTag.setBreadcrumb([
      this.customHeadTag.newBreadcrumItem(
        this.translateService.instant('seo.breadcrumb.item.external')
      ),
    ]);
  }

  public resetFilters(): void {
    this.resultPageFilterService.resetFilters();
  }
}
